import { Button } from '@eventbrite/eds-button';
import { Dropdown } from '@eventbrite/eds-containers';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    AlertChunky,
    ChevronDownChunky,
    CrossChunky,
} from '@eventbrite/eds-iconography';
import { Tag } from '@eventbrite/eds-tag';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { generateSearchUrl } from '../../../../utils/url';
import { useSearchContext } from '../../context';
import { LocationSelectError } from './types';

const Chevron = ({ color = 'ui-800' }) => (
    <Icon type={<ChevronDownChunky />} size="small" color={color} />
);

interface ErrorDropdownContentProps {
    onClose: () => void;
}

const ErrorDropdownContent: React.FunctionComponent<
    ErrorDropdownContentProps
> = (props) => (
    <div className="category-browse-location-picker-error-dropdown">
        <div className="category-browse-location-picker-error-dropdown__close-button">
            <IconButton
                iconType={<CrossChunky />}
                onClick={props.onClose}
                title={gettext('Dismiss dropdown')}
            />
        </div>
        <div className="category-browse-location-picker-error-dropdown__header">
            <Icon type={<AlertChunky />} color="error-text" />
        </div>
        {props.children}
    </div>
);

interface LocationFilterPillProps {
    error: LocationSelectError;
    displayLocation: string;
    clearError: () => void;
    toggleAutocomplete: () => void;
    waitingForLocation: boolean;
}

const buildBBox = ({
    latitude,
    longitude,
}: {
    latitude: number;
    longitude: number;
}) => ({
    sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
    ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
});

const useSearchPath = (error: LocationSelectError) => {
    const { query } = useSearchContext();

    if (error) {
        const currentQuery = {
            ...query,
            places: undefined,
            bbox: buildBBox({
                latitude: error.location.latitude || 0,
                longitude: error.location.longitude || 0,
            }),
        };
        const path = generateSearchUrl({
            currentQuery,
            locationSlug: 'united-states',
        });
        return path;
    }

    return '';
};

export const LocationFilterPill: React.FunctionComponent<
    LocationFilterPillProps
> = (props) => {
    const {
        error,
        displayLocation,
        clearError,
        toggleAutocomplete,
        waitingForLocation,
    } = props;

    const searchPath = useSearchPath(error);

    return (
        <div
            className={`category-browse-location-autocomplete__tag ${
                error?.isError
                    ? 'category-browse-location-autocomplete__tag--error'
                    : ''
            }`}
        >
            <Tag onActiveToggle={toggleAutocomplete} style="outlined">
                <div className="category-browse__location-autocomplete--stub-content">
                    {displayLocation && !waitingForLocation
                        ? displayLocation
                        : gettext('Location')}
                    <Chevron
                        color={error?.isError ? 'ui-red--active' : 'ui-800'}
                    />
                </div>
            </Tag>
            {error?.isError && (
                <Dropdown onClickOutside={clearError}>
                    <ErrorDropdownContent onClose={clearError}>
                        <p
                            style={{
                                textAlign: 'center',
                                paddingBottom: '8px',
                            }}
                        >
                            {gettext(
                                "Sorry, we currently don't support the location you entered. Select a different location or view on map.",
                            )}
                        </p>
                        <Button type="link" href={searchPath} style="link">
                            {gettext('View on map')}
                        </Button>
                    </ErrorDropdownContent>
                </Dropdown>
            )}
        </div>
    );
};
